import React, { useEffect, useMemo } from 'react';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { selectedSizeCart, selectSizeProduct } from '../../redux';
import { clearSizeError, updateCartItemSizeLocal } from '../../redux/slices/shoppingCartSlice';
import { setCurProductSelectedSize } from '../../redux/slices/curProductSlice';
import { useLocation } from 'react-router-dom';
import { SizeSelectorProps } from '../interfaces';
import {
  getCartItemValidationIssue,
  getMaterialForCartItem,
  getSizeAvailabilityMessage,
} from '../../utils/cartValidation';
import { formatSizeLabel } from '../../utils/sizeOptions';
import './sizeSelector.scss';

const getSizeDisplayParts = (size: string) => {
  const trimmedSize = size.trim();
  const sizeWithMeasurement = trimmedSize.match(/^(.+?)\s*\(([^)]+)\)$/);

  if (!sizeWithMeasurement) {
    return {
      primary: trimmedSize,
      secondary: '',
    };
  }

  return {
    primary: sizeWithMeasurement[1].trim(),
    secondary: sizeWithMeasurement[2].trim(),
  };
};
  
const SizeSelector: React.FC<SizeSelectorProps> = ({ itemId, selectedSize, dataSource }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const isCartPage = dataSource === 'cart' || location.pathname.startsWith('/cart');
  const cartItems = useAppSelector(selectedSizeCart);
  const selectedItem = cartItems.find(item => item.id === itemId);
  const cartItem = useAppSelector((state) =>
    state.shoppingCart.cartItems.find((item) => item.id === itemId)
  );
  const materialData = useAppSelector((state) =>
    state.shoppingCart.materialPriceAndImgForCart.find((entry) => entry.itemId === itemId)
  );
  const curProduct = useAppSelector((state) => state.curProduct.productId);
  const selectedProductMaterial = useAppSelector((state) => state.curProduct.selectedMaterial);
  const productMaterialData = useAppSelector((state) => state.curProduct.materialPriceAndImg?.[0]);
  const userId = useAppSelector(state => state.user.user?.id);

  const productMaterial = !isCartPage
    ? productMaterialData?.materialName === selectedProductMaterial
      ? productMaterialData
      : curProduct?.materialPrices?.find(
        (entry) => entry.materialName === selectedProductMaterial
      ) || null
    : null;
  const material = cartItem ? getMaterialForCartItem(cartItem, materialData) : productMaterial;
  const currentIssue = cartItem
    ? getCartItemValidationIssue(cartItem, materialData)
    : null;
  const selectedSizeError = currentIssue?.field === 'size' || currentIssue?.field === 'stock'
    ? currentIssue.message
    : selectedItem?.sizeErrors?.[0];
  const isSizeError = Boolean(selectedSizeError);

  const sizesForCart = selectedItem?.sizes || [];
  const { sizesArray } = useAppSelector(selectSizeProduct);

  const handleSizeChange = (newSize: string) => {
    if (isCartPage && itemId) {
      dispatch(updateCartItemSizeLocal({
        itemId,
        selectedSize: newSize,
        persistGuest: !userId,
      }));

      if (newSize) {
        dispatch(clearSizeError(itemId));
      }
    } else {
      dispatch(setCurProductSelectedSize(newSize));
    }
  };

  const hasAvailableSizes = (sizes: string[]) => sizes && sizes.length > 0;
  const rawSizesToDisplay = isCartPage ? sizesForCart : sizesArray;
  const sizeOptions = useMemo(() => {
    return rawSizesToDisplay
      .map((size) => {
        const unavailableReason = material
          ? getSizeAvailabilityMessage({
            material,
            productId: cartItem?.product.id || curProduct?.id,
            selectedSize: size,
            quantity: cartItem?.quantity || 1,
          })
          : '';

        return {
          size,
          unavailableReason,
        };
      })
      .filter((option) => isCartPage || !option.unavailableReason);
  }, [cartItem?.product.id, cartItem?.quantity, curProduct?.id, isCartPage, material, rawSizesToDisplay]);
  const selectedValue = isCartPage ? selectedItem?.selectedSize || '' : selectedSize || '';

  useEffect(() => {
    if (isCartPage || !selectedValue) return;

    const stillVisible = sizeOptions.some((option) => option.size === selectedValue);
    if (!stillVisible) {
      dispatch(setCurProductSelectedSize(''));
    }
  }, [dispatch, isCartPage, selectedValue, sizeOptions]);

  if (!hasAvailableSizes(rawSizesToDisplay)) {
    return null;
  }

  return (
    <div className="size-selector">
      <label className="input-label" htmlFor="size">Size</label>
      {sizeOptions.length > 0 ? (
        <div className="size-chip-list" role="radiogroup" aria-label="Size">
          {sizeOptions.map(({ size, unavailableReason }) => {
            const isSelected = selectedValue === size;
            const isDisabled = Boolean(unavailableReason);
            const sizeDisplay = getSizeDisplayParts(size);
            const isWideSize = sizeDisplay.primary.length > 4;

            return (
              <button
                key={size}
                type="button"
                className={`size-chip${isWideSize ? ' size-chip--wide' : ''}${isSelected ? ' size-chip--selected' : ''}`}
                disabled={isDisabled}
                aria-checked={isSelected}
                role="radio"
                title={unavailableReason || formatSizeLabel(size)}
                onClick={() => handleSizeChange(size)}
              >
                <span className="size-chip__primary">{sizeDisplay.primary}</span>
                {sizeDisplay.secondary && (
                  <span className="size-chip__secondary">{sizeDisplay.secondary}</span>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="size-selector__empty">
          No sizes in stock for this material.
        </div>
      )}
      {isSizeError && (
        <div className="error-message" style={{ color: 'orange', marginTop: '5px', fontSize: '13px' }}>
          <ExclamationCircleOutlined /> {selectedSizeError}
        </div>
      )}
    </div>
  );
};

export default SizeSelector;
